import style from "./create-template-modal.module.css";
import tracking from "frontend/tracking";
import useOutsideRef from "frontend/utils/click-outside-handler";
import React, { useEffect, useRef, useState } from "react";
import consts from "shared/consts";
import { SyncService } from "frontend/services/syncService";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import FormInputField from "frontend/ui-components/form-fields/form-input-field";
import { useBoardValue } from "frontend/state/board-state";
import FormTextareaField from "frontend/ui-components/form-fields/form-textarea-field";
import { useAtomValue, useSetAtom } from "jotai";
import { stageRefAtom, miniStageRefAtom } from "state-atoms/general-atoms";
import {
  isExportingAtom,
  isPreviewExportModeAtom,
  isStageReadyAtom,
  isThumbnailExportModeAtom,
  posScaleAtom,
} from "state-atoms/stage-atoms";
import StyledButton, { ButtonStyle } from "frontend/ui-components/styled-button";
import { canvasCreateTemplateSchema } from "shared/datamodel/schemas/canvas-template";
import { z } from "zod";
import useStateValue from "frontend/state/value";
import useCanvasTemplates from "frontend/hooks/use-canvas-templates";
import AppLoader from "frontend/loader/app-loader";
import TemplateNotSupportedModal from "./template-not-supported-modal";
import MiniStage from "frontend/canvas-designer-new/mini-stage";
import { getCanvasDataForTemplate } from "frontend/template-creation";
import { isEmpty } from "rambda";
import { getPathPrefix } from "utils/getPathPrefix";

export default function CreateTemplateModal({
  onDismiss,
  syncService,
  isCanvasContainsIntegrationElements,
}: {
  onDismiss: () => void;
  syncService: SyncService<RW> | null;
  isCanvasContainsIntegrationElements: (syncService: SyncService<RW> | null) => Promise<boolean>;
}) {
  const [{ board }] = useBoardValue();
  const [{ user }] = useStateValue();

  const [formValues, setFormValues] = useState<Record<string, any>>({
    name: board?.name,
    tags: [],
    permission: "private",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [creationFailed, setCreationFailed] = useState(false);
  const [isCreating, setCreating] = useState(false);
  const [isContainsIntegrations, setContainsIntegrations] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [showCreateScreen, setShowCreateScreen] = useState(true);

  const isStageReady = useAtomValue(isStageReadyAtom);
  const stageRef = useAtomValue(stageRefAtom);
  const miniStageRef = useAtomValue(miniStageRefAtom);
  const posScale = useAtomValue(posScaleAtom);
  const scale = posScale.scale;
  const setExporting = useSetAtom(isExportingAtom);
  const setPreviewExportMode = useSetAtom(isPreviewExportModeAtom);
  const setThumbnailExportMode = useSetAtom(isThumbnailExportModeAtom);
  const { createTemplate } = useCanvasTemplates();
  const accountName = user?.account?.name;

  const modalRef = useRef(null);
  const isMondayAccount = user?.account?.id === "5";

  const errorMessages: Record<string, string> = {
    name: "Name should not be empty",
    description: "Description should not be empty",
  };

  //We need to disable this detection while a user sees the upgrade modal,
  //otherwise it closes the window when a user tries to click on upgrade plan while upgrading to use a paid template
  useOutsideRef(modalRef, onDismiss, { preventScroll: false });

  useEffect(() => {
    tracking.trackEvent(consts.TRACKING_CATEGORY.TEMPLATES, "create-template-shown", board?.documentId);
    checkIntegrations();
  }, []);

  useEffect(() => {
    if (!isStageReady) {
      return;
    }
    if (formValues.previewImage || formValues.previewImageData) {
      return;
    }
    if (isMondayAccount) {
      setImageDataFromStage();
    }
    setImageData();
  }, [isStageReady, stageRef, posScale]);

  function setProperty(name: string, value: any) {
    setFormValues((values) => ({ ...values, [name]: value }));
  }

  async function setImageData() {
    const data = await getCanvasDataForTemplate(syncService, !isMondayAccount);
    setProperty("data", data);
  }

  async function checkIntegrations() {
    const hasIntegrations = await isCanvasContainsIntegrationElements(syncService);
    setContainsIntegrations(hasIntegrations);
    if (hasIntegrations && !isMondayAccount) {
      setShowCreateScreen(false);
    }
    setLoading(false);
  }

  async function setImageDataFromStage() {
    const thumbnailId = await syncService?.mutate.getThumbnailId();
    setExporting(true);
    // set thumbnail export mode only when we don't have a thumbnail
    setThumbnailExportMode(!thumbnailId);
    try {
      // start async work to prepare thumbnail and preview image
      const thumb = await stageRef?.current?.getSnapshotImage({
        withWatermark: false,
        quality: 1,
        position: posScale,
        scale,
        maxSize: 500,
        elementId: thumbnailId,
        padding: thumbnailId ? -5 : undefined,
      });
      setThumbnailExportMode(false);
      setProperty("thumbnailImageData", thumb);
      setPreviewExportMode(true);
      const preview = await stageRef?.current?.getSnapshotImage({
        withWatermark: false,
        quality: 1,
        position: posScale,
        scale,
        maxSize: 500,
      });
      setProperty("previewImageData", preview);
      setPreviewExportMode(false);
      setExporting(false);
    } catch {
      setThumbnailExportMode(false);
      setPreviewExportMode(false);
      setExporting(false);
    }
  }

  async function handleCreateTemplate() {
    try {
      let updateFormValues;
      if (!isMondayAccount) {
        const url = await miniStageRef?.current?.getSnapshotImage({
          withWatermark: false,
          quality: 1,
          position: miniStageRef.current.getPosition(),
          scale: miniStageRef.current.scaleX(),
          maxSize: 500,
        });
        updateFormValues = { ...formValues, previewImageData: url, thumbnailImageData: url };
      } else {
        updateFormValues = { ...formValues };
      }
      const template = canvasCreateTemplateSchema.parse(updateFormValues);
      setCreating(true);
      const newTemplate = await createTemplate(template);
      tracking.trackEvent(
        consts.TRACKING_CATEGORY.TEMPLATES, // category
        "template_saved_to_lib", // action
        board?.documentId, // string value
        formValues.permission,
        newTemplate.id,
        newTemplate.name
      );
      onDismiss();
    } catch (error) {
      const failedFields: Record<string, string> = {};
      if (error instanceof z.ZodError) {
        const failedPaths = error.issues.map((i) => i.path[0]);
        for (const path of failedPaths) {
          failedFields[path] = errorMessages[path];
        }
        setErrors(failedFields);
      }
      if (isEmpty(failedFields)) {
        tracking.trackAnalyticsEvent("template_creation_failed", {
          category: consts.TRACKING_CATEGORY.TEMPLATES,
          documentId: board?.documentId,
          error: error,
        });
        setCreationFailed(true);
      }
    } finally {
      setCreating(false);
    }
  }

  function onBlurField(id: string) {
    validateField(id);
  }

  function validateField(id: string) {
    setErrors((e) => {
      const { [id]: remove, ...rest } = e;
      try {
        canvasCreateTemplateSchema.parse(formValues);
      } catch (error) {
        if (error instanceof z.ZodError) {
          const failedPaths = error.issues.map((i) => i.path[0]);
          if (failedPaths.includes(id)) {
            rest[id] = errorMessages[id];
          }
        }
      }
      return rest;
    });
  }

  function renderCloseIcon() {
    return (
      <svg
        className={style.closeIcon}
        onClick={onDismiss}
        viewBox="0 0 26 26"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M6.36407 6.36396L19.092 19.0919M19.092 6.36396L6.36407 19.0919"
          stroke="#848199"
          strokeWidth="1.74"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  function renderPermissionOptions() {
    return (
      <div className={style.permissionContainer}>
        <p>Select who can use this template</p>
        <div className={style.permissions}>
          <label>
            <input
              type="radio"
              name="permission"
              value="private"
              checked={formValues.permission === "private"}
              onChange={() => setProperty("permission", "private")}
            />
            Private <span>(Only you)</span>
          </label>
          <label>
            <input
              type="radio"
              name="permission"
              value="account"
              checked={formValues.permission === "account"}
              onChange={() => setProperty("permission", "account")}
            />
            Account <span>{`(Everyone in ${accountName ?? "your account"})`}</span>
          </label>
        </div>
      </div>
    );
  }

  function createTemplateContent() {
    return (
      <>
        <div className={style.fieldsContainer}>
          <div className={style.title}>Create a template</div>
          <FormInputField
            id="name"
            title={"Template name"}
            placeholder="Write here a template name"
            value={formValues.name}
            customError={errors.name ? <div className={style.error}>{errors.name}</div> : null}
            onBlur={onBlurField}
            onChange={(value) => setProperty("name", value)}
            customInputStyle={{ height: 39, fontSize: 12 }}
          />
          <FormTextareaField
            id="description"
            title={"Description"}
            placeholder="Write a description to let others know how and when they can use it"
            value={formValues.description}
            customError={errors.description ? <div className={style.error}>{errors.description}</div> : null}
            onBlur={onBlurField}
            height={57}
            onChange={(value) => setProperty("description", value)}
            customStyle={{ fontSize: 12 }}
          />
          {renderPermissionOptions()}
          <div className={style.buttonContainer} style={{ paddingBottom: 24 }}>
            <StyledButton
              title="Cancel"
              onClick={() => {
                tracking.trackEvent(
                  consts.TRACKING_CATEGORY.TEMPLATES, // category
                  "cancel_template_saved_to_lib", // action
                  board?.documentId, // string value
                  formValues.permission
                );
                onDismiss();
              }}
              style={ButtonStyle.outline}
              customStyle={{
                width: 174,
                fontSize: 16,
                fontWeight: 500,
                height: 41,
              }}
            />
            <StyledButton
              title="Save template"
              onClick={handleCreateTemplate}
              loading={isCreating}
              loadingTitle="Creating template"
              customStyle={{
                width: 174,
                fontSize: 16,
                fontWeight: 500,
                height: 41,
              }}
            />
          </div>
        </div>
        <div className={style.previewContainer}>
          {isContainsIntegrations && isMondayAccount ? (
            <img src={formValues.thumbnailImageData} />
          ) : (
            <div style={{ position: "relative" }}>
              <MiniStage width={375} height={276} templateId={""} templateData={formValues.data} />
            </div>
          )}
        </div>
      </>
    );
  }

  function creationFailedContent() {
    return (
      <div className={style.creationFailedContainer}>
        <img src={getPathPrefix("/images/template-creation-failed.png")} />
        <div className={style.title}>Your template creation has failed</div>
        <div className={style.subtitle}> Please try again shortly </div>
      </div>
    );
  }

  function renderCreateTemplate() {
    return (
      <div className={style.container} ref={modalRef}>
        {renderCloseIcon()}
        {creationFailed ? creationFailedContent() : createTemplateContent()}
      </div>
    );
  }

  function renderTemplateNotSupported() {
    return (
      <TemplateNotSupportedModal
        onDismiss={onDismiss}
        createTemplateWithoutIntegrations={() => setShowCreateScreen(true)}
        boardDocumentId={board?.documentId}
      />
    );
  }

  return isLoading ? <AppLoader /> : showCreateScreen ? renderCreateTemplate() : renderTemplateNotSupported();
}
